import { makeStyles } from "tss-react/mui";
import {
  Bucket,
  IInspectionMenuAction,
  MiInspectionCard,
} from "@miplatform/components";
import {
  IDateRangeISO,
  ISelectOption,
  InspectionActionTypes,
  PopperPlacement,
} from "@miplatform/types";
import { getDate, getDayTitle } from "@miplatform/utils";
import { Grid } from "@mui/material";
import { Box } from "@mui/system";
import {
  MiWorkBoard,
  MiWorkBoardColumn,
  MiWorkBoardHeader,
  MiWorkBoardItem,
} from "../../../components/MiWorkBoard";
import { useInspectionsContext } from "../InspectionsProvider";
import { InspectionAccordion } from "./InspectionAccordion";

export const InspectionBoard = ({
  loading,
  templateFilter = null,
  leftFilterPanelEnabled = false,
  onInspectionClick,
}: {
  loading: boolean;
  templateFilter?: ISelectOption | null;
  leftFilterPanelEnabled?: boolean;
  onInspectionClick?: (action: IInspectionMenuAction) => void;
}) => {
  const { classes } = useStyles();

  const {
    activeCalendar,
    inspectionDays: inspections,
    inspectionTemplates,
    dateRange,
    onDateRangeChange,
    inspectionBuckets: buckets,
    selectedInspectionAttempt,
    showHighlight,
  } = useInspectionsContext();

  const handleInspectionClick = ({
    inspectionAttempt,
    actionType = null,
  }: IInspectionMenuAction) => {
    onInspectionClick?.({
      inspectionAttempt,
      actionType,
    });
  };

  const getDateRangeTitles = (dateRange: IDateRangeISO) => {
    const dayTitles = [];
    dayTitles.push(getDayTitle(dateRange.startDate));

    if (dateRange.endDate) {
      const middleDay = getDate(dateRange.endDate).add(-1, "days");
      dayTitles.push(getDayTitle(middleDay), getDayTitle(dateRange.endDate));
    }
    return dayTitles;
  };

  const threeColGridColumn = { xs: 12, sm: 6, md: 4, lg: 4, xl: 4 };
  const singleColGridColumn = { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 };

  const singleColGridItem = leftFilterPanelEnabled
    ? { xs: 12, sm: 6, md: 3, lg: 2, xl: 2 }
    : { xs: 6, sm: 4, md: 3, lg: 2, xl: 1.5 };
  const threeColGridItem = leftFilterPanelEnabled
    ? { xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }
    : { xs: 6, sm: 12, md: 6, lg: 6, xl: 4 };

  const isMultiColumn = !!dateRange.endDate;
  const columnHeaders = getDateRangeTitles(dateRange);

  return (
    <>
      <MiWorkBoardHeader
        className={classes.boardHeader}
        title={activeCalendar?.name || "Inspections"}
        dateRange={dateRange}
        onDateChange={onDateRangeChange}
      />
      <MiWorkBoard>
        <Grid container spacing={1} flex={1}>
          {columnHeaders.map((date) => (
            <Grid
              size={isMultiColumn ? threeColGridColumn : singleColGridColumn}
              key={date}
            >
              <MiWorkBoardColumn
                title={date}
                loading={loading}
                count={
                  inspections[date]
                    ? Object.values(inspections[date]).reduce(
                        (acc, curr) => acc + curr.length,
                        0
                      )
                    : 0
                }
              >
                {inspections[date] &&
                  Object.keys(inspections[date]).map((templateName) => {
                    if (
                      templateFilter?.value &&
                      templateName !== templateFilter.label
                    ) {
                      return null;
                    }
                    return (
                      <InspectionAccordion
                        key={templateName}
                        count={inspections[date][templateName].length}
                        title={templateName}
                        backgroundColor={
                          inspectionTemplates[templateName]?.color
                        }
                      >
                        <Grid container spacing={2}>
                          {inspections[date][templateName].map((inspection) => (
                            <Grid
                              size={
                                isMultiColumn
                                  ? threeColGridItem
                                  : singleColGridItem
                              }
                              key={inspection.id}
                            >
                              <MiWorkBoardItem
                                onClick={() =>
                                  handleInspectionClick({
                                    inspectionAttempt: inspection,
                                  })
                                }
                                showHighlight={
                                  showHighlight &&
                                  selectedInspectionAttempt?.id ===
                                    inspection.id
                                }
                              >
                                <MiInspectionCard
                                  data={inspection}
                                  headerColor={
                                    inspectionTemplates[templateName]?.color
                                  }
                                  onMenuItemClick={handleInspectionClick}
                                />
                              </MiWorkBoardItem>
                            </Grid>
                          ))}
                        </Grid>
                      </InspectionAccordion>
                    );
                  })}
              </MiWorkBoardColumn>
            </Grid>
          ))}
        </Grid>
      </MiWorkBoard>
      <Box className={classes.bucketContainer}>
        {buckets?.map((bucket) => (
          <Bucket
            key={bucket.name}
            bucket={{
              data: bucket.data,
              ...bucket,
            }}
          >
            {bucket.data?.map((inspection) => (
              <MiWorkBoardItem
                key={inspection.id}
                onClick={() =>
                  onInspectionClick?.({
                    inspectionAttempt: inspection,
                    actionType: InspectionActionTypes.Schedule,
                  })
                }
                showHighlight={
                  showHighlight &&
                  selectedInspectionAttempt?.id === inspection.id
                }
                className={classes.bucketItem}
              >
                <MiInspectionCard
                  data={inspection}
                  headerColor={
                    inspectionTemplates[inspection.templateName]?.color
                  }
                  onMenuItemClick={handleInspectionClick}
                  popperPlacement={PopperPlacement.Top}
                />
              </MiWorkBoardItem>
            ))}
          </Bucket>
        ))}
      </Box>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  boardHeader: {
    position: "sticky",
    zIndex: 1,
  },
  bucketContainer: {
    display: "flex",
    flexDirection: "column",
    position: "sticky",
    bottom: 0,
  },
  bucketItem: {
    maxWidth: 170,
  },
}));
